import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AMENITIES, AmenityCategory, AmenityId } from '../types/amenity';
import { AMENITY_ICONS } from './AmenityIcons';

interface AmenitiesSelectorProps {
  selected: AmenityId[];
  onChange: (amenities: AmenityId[]) => void;
  disabled?: boolean;
}

const CATEGORY_ORDER: AmenityCategory[] = [
  'kitchen',
  'laundry',
  'bathroom',
  'climate',
  'entertainment',
  'sports',
  'outdoor',
  'safety',
  'connectivity',
  'parking',
];

export const AmenitiesSelector: React.FC<AmenitiesSelectorProps> = ({ selected, onChange, disabled }) => {
  const { t } = useTranslation();

  const toggle = (id: AmenityId) => {
    if (selected.includes(id)) {
      onChange(selected.filter((a) => a !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: AMENITIES.filter((a) => a.category === cat),
  }));

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{t('amenities.title')}</Typography>
      {grouped.map(({ category, items }) => (
        <Box key={category}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {t(`amenities.categories.${category}`)}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {items.map((amenity) => {
              const isSelected = selected.includes(amenity.id);
              return (
                <Chip
                  key={amenity.id}
                  icon={AMENITY_ICONS[amenity.id]}
                  label={t(`amenities.items.${amenity.id}`)}
                  size="small"
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  onClick={() => !disabled && toggle(amenity.id)}
                  disabled={disabled}
                  sx={{ cursor: disabled ? 'default' : 'pointer' }}
                />
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
